import { createContext, onCleanup, useContext } from "solid-js";
import { atom } from "../atom/atom";
export interface BlackBoardOption {
    autoCleanUp?: boolean;
    allowSameRegister?: boolean;
    /** 允许后注册覆盖前注册 */
    allowOverride?: boolean;
    onUpdate?: () => void;
}

export const createBlackBoardAtom = <T extends Record<string, any>>(
    baseOpts: BlackBoardOption = {},
) => {
    const bb = createBlackBoard<T>({
        ...baseOpts,
        onUpdate() {
            source(() => bb.store);
            baseOpts.onUpdate?.();
        },
    });
    const source = atom(bb.store, { equals: false });
    return source;
};

/**
 * @zh Blackboard 是用于大型软件中的 api 互通系统，用于解决 Context 不能兄弟互通数据的问题。可以全局注册，也可局部注册
 *
 */
export const createBlackBoard = <T extends Record<string, any>>(
    baseOpts: BlackBoardOption = {},
) => {
    const store = new Map();
    const deleteFn = (name: keyof T) => {
        if (!store.has(name)) throw new Error(`Blackboard app ${name.toString()} isn't init yet`);
        store.delete(name);
        baseOpts.onUpdate?.();
    };
    return {
        store,
        /** 应该在组件声明时进行注册 App，保证在 onMount 时能够获取到数据 */
        register<D extends keyof T>(name: D, api: T[D], opts: BlackBoardOption = {}) {
            const isExist = store.has(name);
            if (isExist && !(opts.allowSameRegister ?? baseOpts.allowSameRegister)) {
                throw new Error("Blackboard has a same app named " + name.toString());
            }
            if (isExist && !opts.allowOverride) return;
            store.set(name, api);
            baseOpts.onUpdate?.();
            if (opts.autoCleanUp ?? baseOpts.autoCleanUp) {
                onCleanup(() => {
                    store.delete(name);
                });
            }
            return store;
        },
        /** 在 onMount 阶段可以获取到所有声明的 App */
        getApp<D extends keyof T>(name: D): T[D] {
            if (!store.has(name))
                throw new Error(`Blackboard app ${name.toString()} isn't init yet`);
            return store.get(name);
        },
        deleteFn,
        /** 检查APP是否注册*/
        check(name: keyof T) {
            return store.has(name);
        },
        destroy() {
            store.clear();
        },
    };
};

/**
 * @zh Context 和 useContext 一体化
 * @example
 * const ctx = createCtx<{
 *      username: '江夏尧',
 *      password: '123456'
 * }>();
 *
 * // parent
 * ()=> <ctx.Provider value={ctx.defaultValue}></ctx.Provider>
 *
 * // child
 * const info = ctx.use()
 *
 *  */
export const createCtx = <T>(data?: T, allowEmpty = false) => {
    const ctx = createContext(data);
    return {
        ...ctx,
        use<D = T>() {
            const context = useContext(ctx);
            if (!allowEmpty && context === undefined) {
                throw new Error("context is empty: place check parent jsx element!");
            }
            return context as D;
        },
    };
};
